"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { ArrowRight, BookOpen, Heart, Layers, Lock, PlayCircle } from "lucide-react";
import { cn } from "@/components/ui/cn";
import { langPath, localeTag, type Dict, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import { formatPrice } from "@/lib/format";
import { enroll, likeModule } from "@/lib/lecturehub/module-actions";
import { ShareActions } from "@/components/ui/ShareActions";
import type { SharePayload } from "./moduleShare";
import { isPaidGate, paidLockKind } from "./paywall";
import type { ModuleAccessLevelEnum } from "@/lib/lecturehub/types";

// Noun-form picker for the stat tiles (1 Lekcija, 2 Lekciji, 5 Lekcij).
// CLDR plural categories via Intl.PluralRules (sl: one/two/few/other) with the
// word forms as Dict keys, so translations control every form. Inline rather
// than from format.ts because format.ts ships full "N noun" strings; the tiles
// here keep number and label on separate visual lines.
function lessonsLabel(n: number, lang: Lang, d: Dict["detail"]): string {
  const forms: Record<Intl.LDMLPluralRule, string> = {
    zero: d.lessonOther,
    one: d.lessonOne,
    two: d.lessonTwo,
    few: d.lessonFew,
    many: d.lessonOther,
    other: d.lessonOther,
  };
  return forms[new Intl.PluralRules(localeTag(lang)).select(n)];
}

type Props = {
  lang: Lang;
  moduleId: number;
  courseSlug: string | null;
  firstLessonId: string | null;
  /** `Module.accessLevel` — who may WATCH; replaces the obsolete `free` flag
   *  as the "is money the gate" test. */
  accessLevel?: ModuleAccessLevelEnum | null;
  /** Costs nothing to unlock. Callers derive it from `price`, not from the
   *  obsolete `Module.free`. */
  free: boolean;
  price: number | null;
  /** Cover image URL (resolved server-side via modulePoster). Null → fallback tile. */
  posterUrl: string | null;
  /** Alt for `posterUrl`, resolved by the same caller (modulePosterAlt). */
  posterAlt: string;
  level: string | null;
  lessonCount: number;
  sectionCount: number;
  canView: boolean;
  enrolled: boolean;
  isAuthed: boolean;
  /** Portal runs in subscription mode (`portal.paymentModel`). Decides the
   *  gated-CTA fallback when the module has no per-module price. */
  isSubscription: boolean;
  share?: SharePayload | null;
  initialLiked: boolean;
  createdAt?: string | null;
};

export function CourseStartCard({
  lang,
  moduleId,
  courseSlug,
  firstLessonId,
  free,
  accessLevel,
  price,
  posterUrl,
  posterAlt,
  level: _level,
  lessonCount,
  sectionCount: _sectionCount,
  canView,
  enrolled: initialEnrolled,
  isAuthed,
  isSubscription,
  share = null,
  initialLiked,
  createdAt,
}: Props) {
  const t = useDict();
  const pt = t.portal;
  const router = useRouter();
  const [enrolled, setEnrolled] = useState(initialEnrolled);
  const [liked, setLiked] = useState(initialLiked);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function loginUrl(): string {
    return `${langPath(lang, "/login")}?redirect=${encodeURIComponent(window.location.pathname)}`;
  }

  function learnUrl(lessonId: string): string {
    return langPath(lang, `/learn/${moduleId}/${courseSlug ?? ""}/${lessonId}`);
  }

  function toggleLike() {
    if (!isAuthed) {
      router.push(loginUrl());
      return;
    }
    const next = !liked;
    setLiked(next);
    startTransition(async () => {
      const r = await likeModule(moduleId, next);
      if (!r.ok) {
        setLiked(!next);
        setError(r.error ?? null);
      }
    });
  }

  function doEnroll() {
    if (!isAuthed) {
      router.push(loginUrl());
      return;
    }
    setError(null);
    startTransition(async () => {
      const r = await enroll(moduleId);
      if (r.ok) {
        // Free-preview case: this card renders inside the learn layout, and a
        // push to a /learn URL we're already on re-renders only the page
        // segment — the layout (this card + the locked sidebar) would keep its
        // stale not-enrolled render. Refresh the whole tree instead; the
        // action's updateTag already invalidated the module cache.
        if (window.location.pathname.includes(`/learn/${moduleId}/`)) {
          setEnrolled(true);
          router.refresh();
          return;
        }
        if (firstLessonId) {
          router.push(learnUrl(firstLessonId));
          return;
        }
        setEnrolled(true);
      } else if (r.code === "MODULE_NOT_ENROLLABLE") {
        // Subscription mode → plans; single mode → per-item checkout.
        // (Same branch as the other start cards. /profil/narocnine does exist on a
        // single-payment portal, but with no subscriptionPlans it renders the
        // FreePortalNotice explainer — nothing purchasable — so checkout is the
        // only destination that can actually unlock the module.)
        router.push(langPath(lang, isSubscription ? "/profil/narocnine" : `/checkout/${moduleId}`));
      } else {
        setError(r.error ?? null);
      }
    });
  }

  // Module.price is already in cents (matches Stripe's amount convention,
  // same as SubscriptionPlanPrice.price). Pass straight to formatPrice.
  const priceCents = price != null && Number(price) > 0 ? Number(price) : null;

  // Editorial CTA — gold pill with warm shadow + inner highlight,
  // matching the dashboard / journey step buttons.
  const ctaClass =
    "inline-flex w-full items-center justify-center gap-2 h-12 rounded-full bg-accent text-accent-foreground font-display font-bold uppercase tracking-[0.12em] text-[12px] md:text-[13px] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:brightness-105 active:translate-y-0 active:brightness-100";
  const ctaStyle = {
    boxShadow:
      "0 2px 4px rgb(0 0 0 / 0.10), 0 8px 18px -6px color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 45%) 45%, transparent), inset 0 1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), white 80%) 70%, transparent), inset 0 -1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 40%) 20%, transparent)",
  };
  const disabledCtaClass =
    "inline-flex w-full items-center justify-center gap-2 h-12 rounded-full bg-accent/40 text-primary/70 font-display font-bold uppercase tracking-[0.12em] text-[12px] md:text-[13px] cursor-not-allowed";

  // Gated-CTA matrix — mirrors PaywallCard's "first match wins" rule, keyed
  // off the portal's payment mode (isSubscription).
  //   1. price > 0                 → "Kupi za €X" (payper; checkout route —
  //                                  addToCart → checkoutCart + Stripe Elements)
  //   2. no price + isSubscription → "Naroči se za ogled" → /profil/narocnine
  //   3. no price + single/free    → disabled "Trenutno nedostopno" (no
  //                                  per-item price set — no path to unlock)
  const lock = paidLockKind(price, isSubscription);

  let cta: React.ReactNode;
  if (enrolled && firstLessonId) {
    cta = (
      <a href={learnUrl(firstLessonId)} className={ctaClass} style={ctaStyle}>
        <PlayCircle className="size-4" aria-hidden />
        {t.detail.continueLearning}
      </a>
    );
  } else if (!canView && isPaidGate({ accessLevel, price, isSubscription })) {
    // Free modules are gated by enrolment, not payment — fall through to
    // the enroll button below (it handles guests via loginUrl + authed
    // users via the enroll action). The paywall matrix only applies to
    // paid content where the user genuinely can't view yet.
    // (Auth-before-money rule — see PaywallCard.tsx's header matrix.)
    if (lock === "buy") {
      // Single module-type-agnostic checkout route — same path for course,
      // ott, podcast, event. verifySession() inside the page redirects
      // guests to login with ?redirect pointing back here.
      const checkoutHref = langPath(lang, `/checkout/${moduleId}`);
      cta = (
        <Link href={checkoutHref} className={ctaClass} style={ctaStyle}>
          <Lock className="size-4" aria-hidden />
          {pt.payperBuyLabel}
        </Link>
      );
    } else if (lock === "subscribe") {
      cta = (
        <Link href={langPath(lang, "/profil/narocnine")} className={ctaClass} style={ctaStyle}>
          <Lock className="size-4" aria-hidden />
          {t.detail.subscribe}
        </Link>
      );
    } else {
      cta = (
        <button type="button" disabled className={disabledCtaClass}>
          <Lock className="size-4" aria-hidden />
          {pt.gatedUnavailable}
        </button>
      );
    }
  } else {
    cta = (
      <button
        type="button"
        onClick={doEnroll}
        disabled={pending}
        className={`${ctaClass} disabled:opacity-60 disabled:pointer-events-none`}
        style={ctaStyle}
      >
        <BookOpen className="size-4" aria-hidden />
        {pending ? "…" : t.detail.enroll}
        <ArrowRight className="size-3.5" aria-hidden />
      </button>
    );
  }

  return (
    // Sticky lives on the parent column wrapper (CourseDetailClient) so this
    // card and the CourseIncludedCard sibling pin and release together —
    // otherwise the included card scrolls *under* this card on long pages.
    <aside className="course-start">
      <div className="course-start__card bg-card text-card-foreground rounded-card border border-border/60 p-6 md:p-7 overflow-hidden">
        {/* Cover — full-bleed across the top of the card (negative margins
            cancel the card padding; the card's overflow-hidden clips it to
            the rounded corners). The detail hero is text-only, so this is the
            course's visual on the right column, not a duplicate. */}
        <div className="course-start__cover relative -mx-6 -mt-6 md:-mx-7 md:-mt-7 mb-5 aspect-[16/9] bg-muted overflow-hidden">
          {posterUrl ? (
            <Image src={posterUrl} alt={posterAlt} fill sizes="360px" className="object-cover" />
          ) : (
            <span className="absolute inset-0 grid place-items-center text-[11px] font-display font-bold uppercase tracking-[0.18em] text-foreground/40">
              {t.detail.typeCourse}
            </span>
          )}
        </div>

        {/* Price block — shown once, prominently. The price is NOT repeated on
            the CTA below. When the user already owns the course we show the
            enrolled status instead of a price. */}
        <div className="course-start__price">
          {enrolled ? (
            <p className="font-display text-[14px] font-extrabold uppercase tracking-[0.18em] text-accent">
              {t.detail.enrolledStatus}
            </p>
          ) : free ? (
            <p className="font-display text-[22px] font-extrabold text-accent">{t.detail.free}</p>
          ) : priceCents ? (
            <>
              <p className="font-display text-[26px] font-extrabold text-foreground tabular-nums tracking-[-0.01em]">
                {formatPrice(priceCents)}
              </p>
              <p className="mt-0.5 text-[11.5px] text-foreground/55">{t.detail.oneTimeLifetime}</p>
            </>
          ) : isSubscription ? (
            <p className="font-display text-[16px] font-bold text-foreground">
              {t.detail.membersOnly}
            </p>
          ) : (
            <p className="font-display text-[12.5px] font-bold uppercase tracking-[0.14em] text-foreground/55">
              {pt.gatedUnavailable}
            </p>
          )}
        </div>

        {/* Like sits as a compact icon to the right of the primary CTA. */}
        <div className="course-start__cta mt-4 flex items-center gap-2">
          <div className="min-w-0 flex-1">{cta}</div>
          <button
            type="button"
            onClick={toggleLike}
            aria-pressed={liked}
            aria-label={liked ? t.detail.liked : t.detail.like}
            title={liked ? t.detail.liked : t.detail.like}
            className={cn(
              "grid size-12 shrink-0 place-items-center rounded-full border transition-colors",
              liked
                ? "border-accent/30 bg-accent/15 text-accent"
                : "border-border/60 text-foreground/55 hover:bg-foreground/[0.04] hover:text-foreground",
            )}
          >
            <Heart className={cn("size-5", liked ? "fill-current" : "")} aria-hidden />
          </button>
          {share ? (
            <ShareActions variant="circle" url={share.url} title={share.title} text={share.text} />
          ) : null}
        </div>

        {/* Compact meta — lessons + last-updated on one muted line so the
            card stays short. */}
        <p className="course-start__meta mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-foreground/55">
          <span className="inline-flex items-center gap-1.5">
            <Layers className="size-3.5 text-foreground/40 shrink-0" aria-hidden />
            <span className="tabular-nums font-semibold text-foreground/80">
              {lessonCount}
            </span>{" "}
            {lessonsLabel(lessonCount, lang, t.detail).toLowerCase()}
          </span>
          {createdAt ? (
            <>
              <span aria-hidden className="text-foreground/25">
                ·
              </span>
              <span>
                {t.detail.updatedLabel} {formatRelative(createdAt, lang)}
              </span>
            </>
          ) : null}
        </p>

        {error ? (
          <p className="course-start__error text-xs text-destructive mt-3">{error}</p>
        ) : null}
      </div>
    </aside>
  );
}

function formatRelative(iso: string, lang: Lang): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const days = Math.floor((Date.now() - t) / 86400000);
  // CLDR-correct for every locale (incl. future runtime languages) — narrow
  // style matches the old compact hand-rolled output ("3d ago" / "danes").
  const rtf = new Intl.RelativeTimeFormat(localeTag(lang), { numeric: "auto", style: "narrow" });
  if (days < 1) return rtf.format(0, "day");
  if (days < 7) return rtf.format(-days, "day");
  if (days < 35) return rtf.format(-Math.floor(days / 7), "week");
  if (days < 365) return rtf.format(-Math.floor(days / 30), "month");
  return rtf.format(-Math.floor(days / 365), "year");
}
